import moment from 'moment';
import type { Observations } from '../../../lib';
import { getDaysBefore } from '../../helpers';
import type { ObservationHeader, ObservationValue } from './types';

export const ObservationTableHeaderViewModeEnum = {
  DATA_ANNOTATION: 'data-annotation',
  PATIENT_DETAILS: 'patient-details',
} as const;
type ObservationTableHeaderViewMode =
  (typeof ObservationTableHeaderViewModeEnum)[keyof typeof ObservationTableHeaderViewModeEnum];

export const transformObservationHeaders = (
  observations: Observations,
  baseEventDate: string,
  viewMode: ObservationTableHeaderViewMode = ObservationTableHeaderViewModeEnum.DATA_ANNOTATION,
): ObservationHeader[] => {
  const buildDateKeys = (
    observations: Observations,
    baseEventDate: string,
    viewMode: ObservationTableHeaderViewMode,
  ) => {
    const dateKeyToEventDate = new Map<string, string>();
    const dateSet = new Set<string>();

    const baseTs = moment(baseEventDate).valueOf();

    for (const observationsForName of Object.values(observations)) {
      observationsForName.forEach((obs) => {
        const obsTs = moment(obs.eventDate).valueOf();
        if (
          viewMode === ObservationTableHeaderViewModeEnum.DATA_ANNOTATION &&
          obsTs > baseTs
        ) {
          return;
        }

        const dateKey = moment(obs.eventDate).format('YYYY-MM-DD');
        dateSet.add(dateKey);

        const existing = dateKeyToEventDate.get(dateKey);
        if (!existing) {
          dateKeyToEventDate.set(dateKey, obs.eventDate);
          return;
        }
        if (obsTs > moment(existing).valueOf()) {
          dateKeyToEventDate.set(dateKey, obs.eventDate);
        }
      });
    }

    const sortedDateKeysAsc = Array.from(dateSet).sort((a, b) => {
      const dateA = moment(a, 'YYYY-MM-DD').valueOf();
      const dateB = moment(b, 'YYYY-MM-DD').valueOf();
      return dateA - dateB;
    });

    if (viewMode !== ObservationTableHeaderViewModeEnum.DATA_ANNOTATION) {
      return { dateKeys: sortedDateKeysAsc, dateKeyToEventDate };
    }

    const lastFive = sortedDateKeysAsc.slice(-5);
    return { dateKeys: lastFive, dateKeyToEventDate };
  };

  let id = 1;
  const result: ObservationHeader[] = [];

  const { dateKeys, dateKeyToEventDate } = buildDateKeys(
    observations,
    baseEventDate,
    viewMode,
  );

  for (const dateKey of dateKeys) {
    const representativeEventDate = dateKeyToEventDate.get(dateKey) || dateKey;
    result.push({
      id: `obs-header-${id++}`,
      label: dateKey,
      date:
        viewMode === ObservationTableHeaderViewModeEnum.DATA_ANNOTATION
          ? getDaysBefore(
              representativeEventDate,
              baseEventDate,
              'data-annotation',
            )
          : moment(dateKey, 'YYYY-MM-DD').format('DD MMM YY'),
    });
  }

  return result;
};

export const transformObservationValues = (
  data: Observations,
  baseEventDate?: string,
  viewMode: ObservationTableHeaderViewMode = ObservationTableHeaderViewModeEnum.PATIENT_DETAILS,
): ObservationValue[] => {
  const effectiveBaseEventDate = baseEventDate || new Date().toISOString();
  const { dateKeys: sortedDateKeys } = (() => {
    const baseTs = moment(effectiveBaseEventDate).valueOf();
    const dateSet = new Set<string>();

    for (const observationsForName of Object.values(data)) {
      observationsForName.forEach((obs) => {
        const obsTs = moment(obs.eventDate).valueOf();
        if (
          viewMode === ObservationTableHeaderViewModeEnum.DATA_ANNOTATION &&
          obsTs > baseTs
        ) {
          return;
        }
        const dateKey = moment(obs.eventDate).format('YYYY-MM-DD');
        dateSet.add(dateKey);
      });
    }

    const sortedAsc = Array.from(dateSet).sort((a, b) => {
      const dateA = moment(a, 'YYYY-MM-DD').valueOf();
      const dateB = moment(b, 'YYYY-MM-DD').valueOf();
      return dateA - dateB;
    });

    if (viewMode !== ObservationTableHeaderViewModeEnum.DATA_ANNOTATION) {
      return { dateKeys: sortedAsc };
    }
    return { dateKeys: sortedAsc.slice(-5) };
  })();

  const buildBestPerDayMap = <T>(
    observations: any[],
    selector: (obs: any) => T,
  ) => {
    const map = new Map<string, { ts: number; value: T }>();
    observations.forEach((obs) => {
      const dateKey = moment(obs.eventDate).format('YYYY-MM-DD');
      const ts = moment(obs.eventDate).valueOf();
      const existing = map.get(dateKey);
      if (!existing || ts > existing.ts) {
        map.set(dateKey, { ts, value: selector(obs) });
      }
    });
    return map;
  };

  const result = Object.entries(data).map(([name, observations]) => {
    const valueMap = buildBestPerDayMap(observations, (obs) =>
      String(obs.value),
    );
    const interpretationMap = buildBestPerDayMap(
      observations,
      (obs) => obs.interpretation,
    );
    const unitMap = buildBestPerDayMap(observations, (obs) => obs.unit);
    const rangeMap = buildBestPerDayMap(observations, (obs) => obs.range);

    const values = sortedDateKeys.map(
      (date) => valueMap.get(date)?.value ?? '-',
    );
    const interpretations = sortedDateKeys.map(
      (date) => interpretationMap.get(date)?.value ?? '-',
    );
    const units = sortedDateKeys.map((date) => unitMap.get(date)?.value ?? '-');
    const ranges = sortedDateKeys.map(
      (date) => rangeMap.get(date)?.value ?? '-',
    );
    return { name, values, interpretations, units, ranges };
  });

  return result;
};

export const isElementOverflowingViewport = (el: Element) => {
  const rect = el.getBoundingClientRect();

  const isOverflowingHorizontally =
    rect.left < 0 || rect.right > window.innerWidth;

  const isOverflowingVertically =
    rect.top < 0 || rect.bottom > window.innerHeight;

  return isOverflowingHorizontally || isOverflowingVertically;
};
